import React, { useEffect, useState } from "react";
import { UnitSystem } from "../../types/Unit";
import {
    getAllUnitSystems,
    createUnitSystem,
    updateUnitSystem,
    deleteUnitSystem,
} from "../../api/UnitAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
}

const UnitSystemSection: React.FC<Props> = ({ token }) => {
    const [items, setItems] = useState<UnitSystem[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Create form
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [createError, setCreateError] = useState<string | null>(null);

    // Inline edit
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editError, setEditError] = useState<string | null>(null);

    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        load();
    }, [token]);

    async function load() {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await getAllUnitSystems(token);
            setItems(data);
        } catch (err) {
            logApiError(err);
            setLoadError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function handleCreate() {
        if (!newName.trim()) { setCreateError("Name is required."); return; }
        setCreateError(null);
        setLoading(true);
        try {
            const created = await createUnitSystem(token, { name: newName.trim(), description: newDesc.trim() || undefined });
            setItems((prev) => [...prev, created]);
            setNewName("");
            setNewDesc("");
        } catch (err) {
            logApiError(err);
            setCreateError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    function startEdit(item: UnitSystem) {
        setEditingId(item.id);
        setEditName(item.name);
        setEditDesc(item.description ?? "");
        setEditError(null);
        setDeleteError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditError(null);
    }

    async function handleSaveEdit(item: UnitSystem) {
        setEditError(null);
        setLoading(true);
        try {
            const updated = await updateUnitSystem(token, {
                id: item.id,
                version: item.version,
                name: editName.trim() || undefined,
                description: editDesc.trim() || undefined,
            });
            setItems((prev) => prev.map((x) => x.id === updated.id ? updated : x));
            setEditingId(null);
        } catch (err) {
            logApiError(err);
            setEditError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(item: UnitSystem) {
        setDeleteError(null);
        setLoading(true);
        try {
            await deleteUnitSystem(token, item.id, item.version);
            setItems((prev) => prev.filter((x) => x.id !== item.id));
            if (editingId === item.id) setEditingId(null);
        } catch (err) {
            logApiError(err);
            setDeleteError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="unit-section">
            <h3>Unit Systems</h3>

            {loadError && <div className="error-message">{loadError}</div>}

            {loading && <div className="status-loading">Loading…</div>}

            <ul className="unit-list">
                {items.map((item) => (
                    <li key={item.id} className="unit-row">
                        {editingId === item.id ? (
                            <div className="unit-edit-form">
                                <div className="unit-field-row">
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="unit-field-row">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={editDesc}
                                        onChange={(e) => setEditDesc(e.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="unit-row-actions">
                                    <button onClick={() => handleSaveEdit(item)} disabled={loading}>Save</button>
                                    <button onClick={cancelEdit} disabled={loading} className="btn-secondary">Cancel</button>
                                </div>
                                {editError && <div className="error-message">{editError}</div>}
                            </div>
                        ) : (
                            <div className="unit-row-view">
                                <span className="unit-name">{item.name}</span>
                                {item.description && <span className="unit-desc">{item.description}</span>}
                                <div className="unit-row-actions" style={{ marginLeft: "auto" }}>
                                    <button onClick={() => startEdit(item)} disabled={loading || !!editingId}>Edit</button>
                                    <button onClick={() => handleDelete(item)} disabled={loading || !!editingId}>Delete</button>
                                </div>
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {deleteError && <div className="error-message">{deleteError}</div>}

            {items.length === 0 && !loading && !loadError && (
                <p style={{ color: "var(--text-muted)" }}>No unit systems yet.</p>
            )}

            <div className="unit-create-form">
                <h4>Add Unit System</h4>
                <div className="unit-field-row">
                    <label>Name</label>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => { setNewName(e.target.value); setCreateError(null); }}
                        placeholder="e.g. SI"
                        disabled={loading}
                    />
                </div>
                <div className="unit-field-row">
                    <label>Description</label>
                    <input
                        type="text"
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        placeholder="Optional description"
                        disabled={loading}
                    />
                </div>
                <button onClick={handleCreate} disabled={loading || !!editingId}>Add</button>
                {createError && <div className="error-message">{createError}</div>}
            </div>
        </div>
    );
};

export default UnitSystemSection;
